'use client';


import { arenaAssetUrl } from './arenaAssetUrl';
import { getAsset } from './assetStore';
import { browserWindow } from './browserSurface';

const UI_SOUNDS = new Set(['click', 'buttonclickrelease', 'menu_select', 'achievement_unlock', 'earnings', 'click_wheel', 'item_drop', 'upgrade_fail', 'jackpot', 'no_profit', 'killchicken']);
export const UI_SOUND_NAMES: readonly string[] = [...UI_SOUNDS];
const ROULETTE_SOUNDS = new Set(['cylinder-spin', 'dry-fire', 'gunshot', 'bullet-load', 'cylinder-close', 'casing-drop']);
// WHY: ?v is the cache key: file paths are stable, so a changed sound without a bump is served stale by browsers/CDN
const SOUND_VERSION = 3;
const ARENA_SOUNDS = new Set(['molotov_ignite', 'molotov_glass', 'fire_extinguish', 'fire_crackle']);
const url = (name: string) =>
  name.startsWith('arena/cs16/') ? arenaAssetUrl(`sounds/${name.slice(6)}.mp3`) :
  name.startsWith('arena/') ? `/sounds/${name}.mp3?v=${SOUND_VERSION}` :
  ARENA_SOUNDS.has(name) ? arenaAssetUrl(`sounds/${name}.mp3`) :
  ROULETTE_SOUNDS.has(name)
    ? `/roulette/${name}.mp3?v=${SOUND_VERSION}`
    : `${UI_SOUNDS.has(name) ? '/sounds' : '/battle'}/${name}.mp3?v=${SOUND_VERSION}`;

export const soundUrl = (name: string): string => url(name);

let ctx: AudioContext | null = null;
// WHY: decoded PCM belongs to the sound, survives context replacement, and needs no retained encoded copy.
const buffers: Record<string, AudioBuffer> = {};
const loading = new Map<string, Promise<void>>();
let unlockWired = false;

export type PlaybackResult = 'requested' | 'played' | 'muted' | 'context-unavailable' | 'context-suspended'
  | 'buffer-missing' | 'below-threshold' | 'voice-limit' | 'preempted' | 'playback-error';
export interface AudioPlaybackPolicy {
  sample: (name: string) => string;
  result: (name: string, result: PlaybackResult) => void;
  admit: (stop: () => void) => (() => void) | null;
}
let playbackPolicy: AudioPlaybackPolicy | null = null;

// WHY: only Arena opts into admission and diagnostics; existing Rush Mid callers keep their playback semantics.
export function withPlaybackPolicy<T>(policy: AudioPlaybackPolicy, play: () => T): T {
  const previous = playbackPolicy;
  playbackPolicy = policy;
  try { return play(); } finally { playbackPolicy = previous; }
}

const ALL_MUTE_KEY = 'sound:all-muted';
let allMuted = false;
try { allMuted = typeof window !== 'undefined' && window.localStorage.getItem(ALL_MUTE_KEY) === '1'; } catch { }
const allMuteListeners = new Set<() => void>();
let masterGain: GainNode | null = null;

export function isAllMuted(): boolean { return allMuted; }
export function subscribeAllMuted(cb: () => void): () => void {
  allMuteListeners.add(cb);
  return () => allMuteListeners.delete(cb);
}
export function setAllMuted(next: boolean): void {
  if (allMuted === next) return;
  allMuted = next;
  try { window.localStorage.setItem(ALL_MUTE_KEY, next ? '1' : '0'); } catch { }
  if (masterGain) masterGain.gain.value = next ? 0 : 1;
  allMuteListeners.forEach((cb) => cb());
}

function getCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  // WHY: mobile Safari/Telegram WebView return a backgrounded context CLOSED; resume() on it throws forever — rebuild it
  if (ctx && ctx.state === 'closed') { ctx = null; masterGain = null; }
  if (!ctx) {
     
    const w: Partial<Window & typeof globalThis> & { webkitAudioContext?: typeof AudioContext } = browserWindow();
    const AC = w.AudioContext ?? w.webkitAudioContext;
     
    if (!AC) return null;
    try { ctx = new AC(); } catch { return null; }
    masterGain = ctx.createGain();
    masterGain.gain.value = allMuted ? 0 : 1;
    masterGain.connect(ctx.destination);
  }
  return ctx;
}

export function wakeAudio(): void {
  const c = getCtx();
  if (!c) return;
  resumeCtx(c);
  if (c.state !== 'running') {
    window.setTimeout(() => { const cc = getCtx(); if (cc) resumeCtx(cc); }, 250);
  }
}

// WHY: buffer and graph preparation must finish even when mute suppresses playback.
export function prepareAudioOutput(prepare: (context: AudioContext, destination: AudioNode) => void): void {
  const context = getCtx();
  if (!context) throw new Error('Web Audio API is unavailable');
  if (!masterGain) throw new Error('Audio output requires the shared master gain');
  prepare(context, masterGain);
}

export function withAudioOutput(play: (context: AudioContext, destination: AudioNode) => void, durationSeconds: number): void {
  const policy = playbackPolicy;
  policy?.result('synth', 'requested');
  if (allMuted) { policy?.result('synth', 'muted'); return; }
  const c = getCtx();
  if (!c) { policy?.result('synth', 'context-unavailable'); return; }
  resumeCtx(c);
  if (policy && c.state !== 'running') { policy.result('synth', 'context-suspended'); return; }
  if (!masterGain) throw new Error('Audio output requires the shared master gain');
  if (!policy) { play(c, masterGain); return; }
  const output = c.createGain();
  output.connect(masterGain);
  const release = policy.admit(() => { output.disconnect(); policy.result('synth', 'preempted'); });
  if (release === null) { output.disconnect(); policy.result('synth', 'voice-limit'); return; }
  try { play(c, output); policy.result('synth', 'played'); }
  catch (error) { output.disconnect(); release(); policy.result('synth', 'playback-error'); throw error; }
  setTimeout(() => { output.disconnect(); release(); }, durationSeconds * 1000);
}

// WHY: not gated on 'suspended': iOS brings the context back as non-standard 'interrupted' after background
function resumeCtx(c: AudioContext): void {
  if (c.state !== 'running') void c.resume().catch(() => undefined);
}

export function wireUnlock(): void {
  if (unlockWired || typeof window === 'undefined') return;
  unlockWired = true;
  window.addEventListener('pointerdown', wakeAudio);
  window.addEventListener('touchend', wakeAudio);
  window.addEventListener('keydown', wakeAudio);
}


function loadBuffer(name: string, readAsset: typeof getAsset = getAsset): Promise<void> {
  const c = getCtx();
  if (!c) return Promise.reject(new Error('Web Audio API is unavailable'));
  if (buffers[name]) return Promise.resolve();
  const pending = loading.get(name);
  if (pending) return pending;
  const promise = readAsset(url(name)).then(async blob => {
    const arr = await blob.arrayBuffer();
    buffers[name] = await c.decodeAudioData(arr);
  }).finally(() => loading.delete(name));
  loading.set(name, promise);
  return promise;
}

export async function loadSounds(names: readonly string[], onUnit?: () => void, readAsset: typeof getAsset = getAsset): Promise<void> {
  wireUnlock();
  const c = getCtx();
  if (!c) throw new Error('Web Audio API is unavailable');
  await Promise.all(names.map((n) => loadBuffer(n, readAsset).then(() => { try { onUnit?.(); } catch { } })));
}

// WHY: Arena bakes short procedural cues at preload and shares the same source lifecycle as decoded samples.
export function registerSound(name: string, buffer: AudioBuffer): void { buffers[name] = buffer; }

export const isLoaded = (name: string): boolean => !!buffers[name];

function prepareSample(name: string, volume: number): { context: AudioContext; buffer: AudioBuffer } | null {
  const policy = playbackPolicy;
  policy?.result(name, 'requested');
  if (allMuted) { policy?.result(name, 'muted'); return null; }
  const context = getCtx();
  if (!context) { policy?.result(name, 'context-unavailable'); return null; }
  const buffer = buffers[policy ? policy.sample(name) : name];
  if (!buffer) { policy?.result(name, 'buffer-missing'); return null; }
  resumeCtx(context);
  if (policy && context.state !== 'running') { policy.result(name, 'context-suspended'); return null; }
  if (policy && volume < .01) { policy.result(name, 'below-threshold'); return null; }
  return { context, buffer };
}

function startSample(name: string, source: AudioBufferSourceNode, disconnect: () => void): (() => void) | undefined {
  const policy = playbackPolicy;
  let ended = false;
  const cleanup = () => {
    if (ended) return;
    ended = true; source.disconnect(); disconnect(); release?.();
  };
  const release = policy?.admit(() => {
    source.stop(); cleanup(); policy.result(name, 'preempted');
  });
  if (release === null) { cleanup(); policy?.result(name, 'voice-limit'); return; }
  source.onended = cleanup;
  try { source.start(); policy?.result(name, 'played'); }
  catch (error) { cleanup(); if (!policy) throw error; policy.result(name, 'playback-error'); }
  return () => { if (!ended) { source.stop(); cleanup(); } };
}

export type SampleOutput = (context: AudioContext, destination: AudioNode, volume: number, bearing: number | null, occluded: boolean) => DirectionalOutput;
let sampleOutput: SampleOutput | null = null;

// WHY: Arena supplies its pooled graph within a cue; other games retain their existing output and admission.
export function withSampleOutput<T>(output: SampleOutput, play: () => T): T {
  const previous = sampleOutput;
  sampleOutput = output;
  try { return play(); } finally { sampleOutput = previous; }
}

export function playSfx(name: string, volume = 1, playbackRate = 1): void {
  const prepared = prepareSample(name, volume);
  if (!prepared) return;
  const { context: c, buffer } = prepared;
  try {
    const src = c.createBufferSource();
    src.buffer = buffer;
    src.playbackRate.value = playbackRate;
    if (sampleOutput) {
      const output = sampleOutput(c, masterGain ?? c.destination, volume, null, false);
      src.connect(output.input); startSample(name, src, output.disconnect); return;
    }
    const g = c.createGain();
    g.gain.value = volume;
    src.connect(g).connect(masterGain ?? c.destination);
    startSample(name, src, () => g.disconnect());
  } catch { playbackPolicy?.result(name, 'playback-error'); }
}

export async function playLoop(name: string, volume = 0.35): Promise<() => void> {
  const c = getCtx();
  if (!c) return () => undefined;
  if (!buffers[name]) {
    try { await loadBuffer(name); } catch { return () => undefined; }
  }
  const buf = buffers[name];
  if (!buf) return () => undefined;
  resumeCtx(c);
  let src: AudioBufferSourceNode | null = null;
  let gain: GainNode | null = null;
  try {
    src = c.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    gain = c.createGain();
    gain.gain.value = volume;
    src.connect(gain).connect(masterGain ?? c.destination);
    src.start();
  } catch { return () => undefined; }
  return () => {
    try { src.stop(); } catch { }
    try { src.disconnect(); } catch { }
    try { gain.disconnect(); } catch { }
  };
}

export interface DirectionalOutput {
  input: AudioNode;
  disconnect: () => void;
}

export function connectDirectionalOutput(
  context: AudioContext, destination: AudioNode, volume: number, bearing: number, occluded: boolean,
): DirectionalOutput {
  const gain = context.createGain();
  gain.gain.value = volume * (occluded ? 2 / 3 : 1);
  const pan = context.createStereoPanner();
  pan.pan.value = Math.sin(bearing);
  gain.connect(pan).connect(destination);
  const filter = occluded ? context.createBiquadFilter() : null;
  if (filter) {
    filter.type = 'lowpass';
    filter.frequency.value = 1200;
    filter.connect(gain);
  }
  return {
    input: filter ?? gain,
    disconnect: () => { filter?.disconnect(); gain.disconnect(); pan.disconnect(); },
  };
}

export function playDirectionalSfx(
  name: string, volume: number, bearing: number, occluded = false, playbackRate = 1,
): (() => void) | undefined {
  const prepared = prepareSample(name, volume * (occluded ? 2 / 3 : 1));
  if (!prepared) return;
  const { context: c, buffer } = prepared;
  const source = c.createBufferSource(); source.buffer = buffer;
  source.playbackRate.value = playbackRate;
  const output = (sampleOutput ?? connectDirectionalOutput)(c, masterGain ?? c.destination, volume, bearing, occluded);
  source.connect(output.input);
  return startSample(name, source, output.disconnect);
}
